import { lazy, Suspense, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { AlertTriangle, Info, Lock, Star } from 'lucide-react'
import LensBand from '../components/lens/LensBand'
import LoginGate from '../components/common/LoginGate'
import ProAnfrageKnopf from '../components/common/ProAnfrageKnopf'
import ProGate from '../components/common/ProGate'
import { Fehler, Laden, SkeletonZeilen } from '../components/common/States'
import { useAuth } from '../contexts/AuthContext'
import { useAktie, useWatchlist, useWatchlistToggle } from '../hooks/useQualyz'
import { ApiError, fehlerText } from '../lib/api'
import { LENSES, formatDatum, formatDezimal, type LensKey } from '../lib/lenses'
import type { AktieAntwort, AktieKriterium, AktieLinse, Idea } from '../services/qualyz'
import '../styles/aktie.css'

// Chart.js nur laden, wenn jemand eine Einzelansicht oeffnet.
const Kursgrafik = lazy(() => import('../components/aktie/Kursgrafik'))
const Jahresbalken = lazy(() => import('../components/aktie/Jahresbalken'))

const REGIONEN = (() => {
  try {
    return new Intl.DisplayNames(['de'], { type: 'region' })
  } catch {
    return null
  }
})()

function landName(code: string | null): string | null {
  if (!code) return null
  try {
    return REGIONEN?.of(code.toUpperCase()) ?? code
  } catch {
    return code
  }
}

/** Die Risikoklassen kommen ASCII aus dem Backend (lenses/epv.py). */
const RISIKOKLASSE: Record<string, string> = {
  stabil: 'stabil',
  normal: 'normal',
  erhoeht: 'erhöht',
  spekulativ: 'spekulativ',
  vorgegeben: 'vorgegeben',
}

function prozent(wert: number | null | undefined, stellen = 1): string {
  return typeof wert === 'number' && Number.isFinite(wert) ? `${formatDezimal(wert, stellen)} %` : '–'
}

function mitVorzeichen(wert: number, stellen = 1): string {
  return `${wert > 0 ? '+' : ''}${formatDezimal(wert, stellen)}`
}

/** 2,2 Mrd. USD · 439,9 Mio. USD */
function kompakt(wert: number | null | undefined, einheit = 'USD'): string {
  if (typeof wert !== 'number' || !Number.isFinite(wert)) return '–'
  const betrag = Math.abs(wert)
  if (betrag >= 1e12) return `${formatDezimal(wert / 1e12, 2)} Bio. ${einheit}`
  if (betrag >= 1e9) return `${formatDezimal(wert / 1e9, 1)} Mrd. ${einheit}`
  return `${formatDezimal(wert / 1e6, 1)} Mio. ${einheit}`
}

/** Betraege der Tabellen in Millionen, kleine Werte mit einer Stelle. */
function mio(wert: number | null | undefined): string {
  if (typeof wert !== 'number' || !Number.isFinite(wert)) return '–'
  const m = wert / 1e6
  return formatDezimal(m, Math.abs(m) < 100 ? 1 : 0)
}

/** Die Einzelansicht liefert die Linsen als Objekt -- das Band erwartet die Form einer Idee. */
function alsIdee(d: AktieAntwort): Idea {
  const idee: Record<string, unknown> = {
    symbol: d.titel.symbol,
    name: d.titel.name,
    sector: d.titel.sektor,
    market_cap: d.kennzahlen.marktwert_usd,
    conviction: d.bewertung.konviktion,
    lenses_judged: d.bewertung.linsen_urteilend,
    lenses_total: d.bewertung.linsen_gesamt,
    lenses_selected: null,
    avg_score: d.bewertung.schnitt,
  }
  for (const linse of LENSES) {
    const l = d.bewertung.linsen[linse.key]
    if (!l) continue
    idee[linse.key] = l.punkte
    idee[`${linse.key}_verdict`] = l.urteil
    idee[`${linse.key}_coverage`] = l.abdeckung
  }
  return idee as unknown as Idea
}

function veraenderungEinJahr(reihe: AktieAntwort['kursreihe']): number | null {
  if (reihe.length < 2) return null
  const [letztesDatum, letzterKurs] = reihe[reihe.length - 1]
  const grenze = new Date(`${letztesDatum}T12:00:00`).getTime() - 365 * 86_400_000
  const start = reihe.find(([d]) => new Date(`${d}T12:00:00`).getTime() >= grenze)
  if (!start || start[1] <= 0) return null
  return (letzterKurs / start[1] - 1) * 100
}

export default function AktiePage() {
  const { symbol = '' } = useParams<{ symbol: string }>()
  const aktie = useAktie(symbol)
  const [reiter, setReiter] = useState<'kurz' | 'zahlen'>('kurz')

  if (aktie.isLoading) {
    return (
      <div className="q-page">
        <Laden text={`${symbol.toUpperCase()} wird bewertet …`} />
      </div>
    )
  }
  if (aktie.error instanceof ApiError && aktie.error.status === 401) {
    return (
      <div className="q-page">
        <section className="q-card">
          <LoginGate titel="Die Einzelansicht gibt es nach der kostenlosen Anmeldung." zeilen={7} />
        </section>
      </div>
    )
  }
  if (aktie.error || !aktie.data) {
    return (
      <div className="q-page">
        <Fehler fehler={aktie.error} nochmal={() => aktie.refetch()} />
      </div>
    )
  }

  const d = aktie.data
  return (
    <div className="q-page qa-seite">
      <Kopf d={d} reiter={reiter} setReiter={setReiter} />
      {reiter === 'kurz' ? <Kurzuebersicht d={d} /> : <Zahlen d={d} />}
    </div>
  )
}

function Kopf({ d, reiter, setReiter }: { d: AktieAntwort; reiter: 'kurz' | 'zahlen'; setReiter: (r: 'kurz' | 'zahlen') => void }) {
  const { isAuthenticated } = useAuth()
  const jahr = veraenderungEinJahr(d.kursreihe)
  const meta = [d.titel.boerse, landName(d.titel.land), d.titel.sektor].filter(Boolean).join(' · ')
  return (
    <section className="q-card">
      <div className="qa-kopf">
        <div>
          <div className="qa-titel">
            <span className="q-sym">{d.titel.symbol}</span>
            <span className="qa-firma">{d.titel.name}</span>
          </div>
          <div className="qa-meta">
            {meta}
            {d.titel.isin && (
              <>
                {meta ? ' · ' : ''}ISIN <b>{d.titel.isin}</b>
              </>
            )}
          </div>
        </div>
        <div className="qa-kursblock">
          <div className="qa-kurs">
            <div className="qa-kurs-wert">
              {formatDezimal(d.kurs.wert, 2)}
              <span className="qa-kurs-einheit">{d.titel.waehrung}</span>
            </div>
            <div className="qa-kurs-stand">
              {jahr !== null && (
                <>
                  <span className={`qa-veraenderung ${jahr >= 0 ? 'is-plus' : 'is-minus'}`}>{mitVorzeichen(jahr)} %</span> in 1 Jahr ·{' '}
                </>
              )}
              Stand {formatDatum(d.kurs.stand)}
            </div>
          </div>
          {isAuthenticated && <Merken symbol={d.titel.symbol} />}
        </div>
      </div>
      <div className="qa-tabs" role="tablist" aria-label="Ansicht">
        <button type="button" role="tab" className="qa-tab" aria-selected={reiter === 'kurz'} onClick={() => setReiter('kurz')}>
          Kurzübersicht
        </button>
        <button type="button" role="tab" className="qa-tab" aria-selected={reiter === 'zahlen'} onClick={() => setReiter('zahlen')}>
          Zahlen
        </button>
      </div>
    </section>
  )
}

function Merken({ symbol }: { symbol: string }) {
  const liste = useWatchlist()
  const umschalten = useWatchlistToggle()
  const gemerkt = !!liste.data?.items.some((i) => i.symbol === symbol)
  return (
    <div>
      <button
        type="button"
        className="q-btn q-btn-ghost q-btn-sm"
        aria-pressed={gemerkt}
        disabled={umschalten.isPending || liste.isLoading}
        onClick={() => umschalten.mutate({ symbol, merken: !gemerkt })}
      >
        <Star aria-hidden="true" className={gemerkt ? 'qa-stern-voll' : undefined} />
        <span>{gemerkt ? 'Gemerkt' : 'Merken'}</span>
      </button>
      {umschalten.error && <div className="qa-merken-fehler">{fehlerText(umschalten.error)}</div>}
    </div>
  )
}

function Kurzuebersicht({ d }: { d: AktieAntwort }) {
  const r = d.risiko
  return (
    <div className="qa-ansicht">
      <section className="qa-drei">
        <div className="q-panel">
          <div className="q-cap">Konviktion</div>
          <span className="q-conv q-conv--lg">
            <span className="q-conv-num">
              {d.bewertung.konviktion}
              <span className="q-conv-von">/{d.bewertung.linsen_gesamt}</span>
            </span>
          </span>
          <div className="qa-klein">
            {d.bewertung.rang !== null && (
              <>
                Rang <b>{d.bewertung.rang}</b> im Screener ·{' '}
              </>
            )}
            Ø Score <b>{formatDezimal(d.bewertung.schnitt, 1)}</b>
          </div>
        </div>
        <div className="q-panel">
          <div className="q-cap">Die sieben Linsen</div>
          <LensBand idea={alsIdee(d)} />
          <div className="qa-klein">
            {d.bewertung.linsen_urteilend} von {d.bewertung.linsen_gesamt} Linsen urteilen
          </div>
        </div>
        <div className="q-panel">
          <div className="q-cap">Risiko und Position</div>
          <div className="qa-zahl">{r.max_position_pct === null ? '–' : `${formatDezimal(r.max_position_pct, 2)} %`}</div>
          <div className="qa-klein">
            max. Position bei <b>{formatDezimal(r.risikobudget_pct, 0)} %</b> Risikobudget
          </div>
          <div className="qa-klein">
            Vola 3 J <b>{prozent(r.vola_3j_pct)}</b> · größter Rückgang <b>{prozent(r.max_drawdown_pct)}</b>
          </div>
        </div>
      </section>

      <Suspense
        fallback={
          <section className="q-card">
            <SkeletonZeilen anzahl={6} />
          </section>
        }
      >
        <Kursgrafik
          reihe={d.kursreihe}
          dividenden={d.dividenden}
          naechsteZahlen={d.naechste_zahlen}
          naechsteGeschaetzt={d.naechste_zahlen_termin?.geschaetzt ?? false}
          epv={d.epv_je_aktie}
          fairwerte={d.fairwerte}
          marken={d.quartalsmarken}
          waehrung={d.titel.waehrung}
        />
      </Suspense>

      <Kennzahlen d={d} />

      {d.hinweise.length > 0 && (
        <section className="q-card" aria-label="Hinweise zu den Daten">
          <div className="q-card-head">
            <h3 className="q-card-title">Hinweise zu den Daten</h3>
          </div>
          <div className="qa-hinweise">
            {d.hinweise.map((h) => (
              <p key={h.schluessel}>
                <Info aria-hidden="true" />
                <span>{h.text}</span>
              </p>
            ))}
          </div>
        </section>
      )}

      <section className="q-card">
        <div className="q-card-head">
          <h3 className="q-card-title">Warum dieses Urteil</h3>
        </div>
        {d.begruendung ? (
          <Begruendung begruendung={d.begruendung} waehrung={d.titel.waehrung} />
        ) : (
          <BegruendungGesperrt stufe={d.stufe} />
        )}
      </section>
    </div>
  )
}

function Kennzahlen({ d }: { d: AktieAntwort }) {
  const k = d.kennzahlen
  const kacheln: [string, string][] = [
    ['Marktwert', kompakt(k.marktwert_usd)],
    ['KGV', k.kgv !== null ? formatDezimal(k.kgv, 1) : k.verlust ? 'Verlust' : '–'],
    ['KUV', k.kuv !== null ? formatDezimal(k.kuv, 1) : '–'],
    ['Dividendenrendite', prozent(k.dividendenrendite_pct)],
    ['Umsatzwachstum', k.umsatzwachstum_pct !== null ? `${mitVorzeichen(k.umsatzwachstum_pct)} %` : '–'],
    ['Nettomarge', prozent(k.nettomarge_pct)],
    ['Eigenkapitalquote', prozent(k.eigenkapitalquote_pct, 0)],
    [
      d.naechste_zahlen_termin?.geschaetzt ? 'Nächste Zahlen (geschätzt)' : 'Nächste Zahlen',
      formatDatum(d.naechste_zahlen),
    ],
  ]
  const fussnote = [
    k.geschaeftsjahr_ende ? `Geschäftsjahr bis ${formatDatum(k.geschaeftsjahr_ende)}` : null,
    'Dividende der letzten 12 Monate',
    k.fx_rate && k.berichtswaehrung
      ? `KGV und KUV mit 1 ${k.berichtswaehrung} = ${formatDezimal(k.fx_rate, 4)} USD umgerechnet`
      : null,
  ]
    .filter(Boolean)
    .join(', ')
  return (
    <>
      <section className="q-chips qa-kacheln" aria-label="Kennzahlen">
        {kacheln.map(([name, wert]) => (
          <div key={name} className="q-chip">
            <div className="q-chip-k">{name}</div>
            <div className="q-chip-v">{wert}</div>
          </div>
        ))}
      </section>
      <p className="qa-hinweis">{fussnote}.</p>
    </>
  )
}

function kriteriumWert(k: AktieKriterium, waehrung: string): string {
  if (k.wert_text) return k.wert_text
  if (k.wert === null) return 'keine Daten'
  const w = k.wert * (k.skala || 1)
  switch (k.einheit) {
    case '%':
      return `${formatDezimal(w, 1)} %`
    case 'Prozentpunkte':
      return `${mitVorzeichen(w)} pp`
    case 'USD':
      return kompakt(w, waehrung === 'USD' ? 'USD' : 'USD')
    default:
      return formatDezimal(w, Math.abs(w) >= 10 || Number.isInteger(w) ? 0 : 2)
  }
}

function Begruendung({ begruendung, waehrung }: { begruendung: Record<LensKey, AktieLinse>; waehrung: string }) {
  return (
    <div className="qa-linsen">
      {LENSES.map((linse) => {
        const b = begruendung[linse.key]
        if (!b) return null
        const zustand = b.abdeckung < 0.5 ? 'na' : b.urteil.toLowerCase()
        return (
          <div key={linse.key} className="qa-linse">
            <div className="qa-linse-kopf">
              <div className="q-band q-band--sm">
                <div className={`q-cell q-cell--${zustand}`}>
                  <span className="q-cell-code">{linse.code}</span>
                </div>
              </div>
              <span className="qa-linse-titel">{linse.label}</span>
              <span className="qa-linse-urteil">
                {zustand === 'na' ? 'kein Signal' : `${b.urteil} · ${Math.round(b.punkte)} Punkte`}
              </span>
            </div>
            {b.kriterien.map((k) => {
              const anteil = k.punkte !== null && k.max_punkte > 0 ? (k.punkte / k.max_punkte) * 100 : 0
              const titel = `${k.regel}${k.punkte !== null ? ` Hier: ${formatDezimal(k.punkte, 1)} von ${k.max_punkte} Punkten.` : ''}`
              return (
                <div key={k.schluessel} className={`qa-krit${k.fehlt ? ' is-leer' : ''}`} title={titel}>
                  <span>{k.name}</span>
                  <span className="qa-krit-balken" aria-hidden="true">
                    <i style={{ width: `${Math.max(0, Math.min(100, anteil))}%` }} />
                  </span>
                  <b>{kriteriumWert(k, waehrung)}</b>
                </div>
              )
            })}
            {linse.key === 'epv' && b.details && (b.details.epv_je_aktie !== null || b.details.fehlt_grund) && (
              <div className="qa-krit-details">
                {b.details.epv_je_aktie !== null ? (
                  <>
                    Ertragswert <b>{formatDezimal(b.details.epv_je_aktie, 2)} {waehrung}</b> je Aktie · Kapitalkosten{' '}
                    <b>{prozent(b.details.kapitalkosten_pct, 0)}</b> (
                    {RISIKOKLASSE[b.details.risikoklasse ?? ''] ?? b.details.risikoklasse})
                  </>
                ) : (
                  <>Nicht bewertbar: {b.details.fehlt_grund}</>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function BegruendungGesperrt({ stufe }: { stufe: AktieAntwort['stufe'] }) {
  const vorschau = (
    <div className="qa-linsen">
      {LENSES.slice(0, 4).map((l) => (
        <div key={l.key} className="qa-linse">
          <SkeletonZeilen anzahl={4} />
        </div>
      ))}
    </div>
  )
  if (stufe === 'besucher') return <LoginGate titel="Die Begründung je Linse gibt es mit Pro." vorschau={vorschau} />
  return <ProGate titel="Die Begründung je Linse gibt es mit Pro." vorschau={vorschau} />
}

interface TabellenZeile<T> {
  name: string
  wert: (spalte: T) => ReactNode
}

function Tabelle<T>({ titel, spalten, kopf, zeilen }: { titel: string; spalten: T[]; kopf: (s: T) => string; zeilen: TabellenZeile<T>[] }) {
  return (
    <section className="q-card">
      <div className="q-card-head">
        <h3 className="q-card-title">{titel}</h3>
      </div>
      <div className="q-table-wrap">
        <table className="q-table qa-tabelle">
          <thead>
            <tr>
              <th scope="col">in Mio.</th>
              {spalten.map((s, i) => (
                <th key={i} scope="col">
                  {kopf(s)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {zeilen.map((z) => (
              <tr key={z.name}>
                <th scope="row">{z.name}</th>
                {spalten.map((s, i) => (
                  <td key={i}>{z.wert(s)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function Zahlen({ d }: { d: AktieAntwort }) {
  const jahre = d.zahlen.jahre
  const pro = d.stufe === 'pro'
  const waehrungen = new Set(jahre.map((j) => j.reported_currency).filter(Boolean))
  const waehrung = jahre[0]?.reported_currency ?? d.kennzahlen.berichtswaehrung ?? 'USD'
  const gemischt = waehrungen.size > 1
  const jahrKopf = (j: (typeof jahre)[number]) => {
    const jahr = j.date.slice(0, 4)
    return gemischt && j.reported_currency ? `${jahr} · ${j.reported_currency}` : jahr
  }
  const endMonat = jahre[0] ? Number(jahre[0].date.slice(5, 7)) : 12
  const monatsname = new Date(2000, endMonat - 1, 1).toLocaleString('de-DE', { month: 'long' })
  const zb = d.zahlen.zwischenberichte

  if (!jahre.length) {
    return (
      <div className="qa-ansicht">
        <section className="q-card">
          <p className="qa-hinweise">Für diesen Titel liegen keine Abschlüsse vor.</p>
        </section>
      </div>
    )
  }

  return (
    <div className="qa-ansicht">
      {!pro && (
        <div className="q-alert" role="note">
          <Lock aria-hidden="true" />
          <span>Kostenlos siehst du das letzte Geschäftsjahr. Zehn Jahre, Zwischenberichte und die Grafik gibt es mit Pro.</span>
          {d.stufe === 'kostenlos' ? (
            <span style={{ marginLeft: 'auto' }}>
              <ProAnfrageKnopf />
            </span>
          ) : (
            <Link className="q-btn q-btn-primary q-btn-sm" to="/register" style={{ marginLeft: 'auto' }}>
              Registrieren
            </Link>
          )}
        </div>
      )}
      <p className="qa-hinweis" style={{ marginTop: 0 }}>
        Beträge in Millionen {waehrung}, der Berichtswährung.
        {endMonat !== 12 && ` Das Geschäftsjahr endet im ${monatsname}.`}
        {gemischt && ' Die Berichtswährung hat gewechselt; jede Spalte nennt ihre Währung.'} Kurs und Marktwert in{' '}
        {d.titel.waehrung}.
      </p>

      {pro && jahre.length > 1 && (
        <section className="q-card">
          <div className="q-card-head qa-card-kopf">
            <h3 className="q-card-title">Umsatz und Nettogewinn</h3>
            <div className="qa-legende">
              <span>
                <i className="is-hell" />
                Umsatz
              </span>
              <span>
                <i />
                Nettogewinn
              </span>
            </div>
          </div>
          <div className="qa-chartbox">
            <Suspense fallback={<SkeletonZeilen anzahl={5} />}>
              <Jahresbalken jahre={jahre} />
            </Suspense>
          </div>
        </section>
      )}

      <Tabelle
        titel="Gewinn und Verlust"
        spalten={jahre}
        kopf={jahrKopf}
        zeilen={[
          { name: 'Umsatz', wert: (j) => mio(j.revenue) },
          { name: 'Umsatzwachstum', wert: (j) => (j.umsatzwachstum_pct !== null ? `${mitVorzeichen(j.umsatzwachstum_pct)} %` : '–') },
          { name: 'Umsatzkosten', wert: (j) => mio(j.cost_of_revenue) },
          { name: 'Kostenquote', wert: (j) => prozent(j.kostenquote_pct) },
          { name: 'Bruttogewinn', wert: (j) => mio(j.gross_profit) },
          { name: 'Bruttomarge', wert: (j) => prozent(j.bruttomarge_pct) },
          { name: 'Nettogewinn', wert: (j) => mio(j.net_income) },
          { name: 'Nettomarge', wert: (j) => prozent(j.nettomarge_pct) },
        ]}
      />
      <Tabelle
        titel="Bilanz"
        spalten={jahre}
        kopf={jahrKopf}
        zeilen={[
          { name: 'Bilanzsumme', wert: (j) => mio(j.total_assets) },
          { name: 'Barmittel', wert: (j) => mio(j.cash_and_short_term) },
          { name: 'Umlaufvermögen', wert: (j) => mio(j.total_current_assets) },
          { name: 'Kurzfristige Verbindlichkeiten', wert: (j) => mio(j.total_current_liabilities) },
          { name: 'Langfristige Schulden', wert: (j) => mio(j.long_term_debt) },
          { name: 'Eigenkapital', wert: (j) => mio(j.total_equity) },
          { name: 'Eigenkapitalquote', wert: (j) => prozent(j.eigenkapitalquote_pct, 0) },
          { name: 'Liquidität 3. Grades', wert: (j) => (j.liquiditaet_3 !== null ? `${formatDezimal(j.liquiditaet_3, 2)} ×` : '–') },
        ]}
      />
      <Tabelle
        titel="Cashflow"
        spalten={jahre}
        kopf={jahrKopf}
        zeilen={[
          { name: 'Operativer Cashflow', wert: (j) => mio(j.operating_cash_flow) },
          { name: 'Investitionen', wert: (j) => mio(j.investitionen) },
          { name: 'Free Cashflow', wert: (j) => mio(j.free_cash_flow) },
          { name: 'FCF-Marge', wert: (j) => prozent(j.fcf_marge_pct) },
        ]}
      />

      {pro && zb && zb.perioden.length > 0 && (
        <>
          <Tabelle
            titel={`Zwischenberichte (${zb.takt_monate === 6 ? 'halbjährlich' : zb.takt_monate === 3 ? 'quartalsweise' : 'unterjährig'})`}
            spalten={zb.perioden}
            kopf={(p) => p.name}
            zeilen={[
              {
                name: 'Umsatz',
                wert: (p) =>
                  p.unstimmig ? (
                    <span className="q-pos-val">
                      {mio(p.revenue)}
                      <span className="q-hint" title="Dieser Wert passt nicht zur übrigen Reihe und bleibt beim Vorjahresvergleich außen vor.">
                        <AlertTriangle aria-label="Wert passt nicht zur Reihe" />
                      </span>
                    </span>
                  ) : (
                    mio(p.revenue)
                  ),
              },
              {
                name: 'Umsatz zum Vorjahr',
                wert: (p) => (p.umsatz_zum_vorjahr_pct !== null ? `${mitVorzeichen(p.umsatz_zum_vorjahr_pct)} %` : '–'),
              },
              { name: 'Nettogewinn', wert: (p) => mio(p.net_income) },
              { name: 'Nettomarge', wert: (p) => prozent(p.nettomarge_pct) },
            ]}
          />
          {zb.perioden.some((p) => p.unstimmig) && (
            <p className="qa-hinweis">
              Markierte Umsätze passen nicht zur übrigen Reihe. Sie stehen so in den Rohdaten und bleiben beim
              Vorjahresvergleich außen vor.
            </p>
          )}
        </>
      )}
      {!pro && (
        <ProGateOderLogin
          stufe={d.stufe}
          titel="Zehn Jahre und Zwischenberichte gibt es mit Pro."
        />
      )}
    </div>
  )
}

function ProGateOderLogin({ stufe, titel }: { stufe: AktieAntwort['stufe']; titel: string }) {
  const vorschau = (
    <section className="q-card">
      <SkeletonZeilen anzahl={5} />
    </section>
  )
  return stufe === 'besucher' ? <LoginGate titel={titel} vorschau={vorschau} /> : <ProGate titel={titel} vorschau={vorschau} />
}
